/*!
 * cm-menu-tap-fix.js
 * ---------------------------------------------------------------------------
 * HARTER FIX: Navigation im mobilen Header-Drawer beim ERSTEN Tap.
 *
 * Problem im Theme:
 *   Im geoeffneten Menue-Drawer passierte beim ersten Antippen eines Links
 *   (z.B. "Outerwear", "Shoes") nichts - erst der zweite Tap navigierte.
 *   Ursache ist ein Zusammenspiel aus mehreren Handlern, die den ersten
 *   Klick abfangen: der Focus-Trap (capture-phase focusin in focus.js), die
 *   globale Event-Delegation in component.js, die <details>-Toggle-Logik
 *   des Drawers und - auf Touch-Geraeten - die Hover-Emulation.
 *   Alle bisherigen Versuche, das an der Wurzel zu reparieren, sind
 *   gescheitert, weil jeder dieser Handler fuer sich genommen "richtig" ist.
 *
 * Loesung hier:
 *   Eine komplett eigene, vom Theme unabhaengige Navigationslogik. Sie haengt
 *   sich in der CAPTURE-Phase ganz vorne in die Event-Kette (also VOR allen
 *   Theme-Handlern) und erzwingt bei einem Tap auf einen Drawer-Link sofort
 *   die Weiterleitung. Der Rest der Kette wird abgebrochen, damit niemand
 *   mehr dazwischenfunken kann.
 *
 * Drei Sicherheitsnetze, damit es IMMER greift:
 *   1. pointerup  (capture) - schnellster Pfad, feuert vor dem click
 *   2. click      (capture) - Backstop fuer Maus/Tastatur
 *   3. touchend   (capture) - Fallback fuer Browser ohne Pointer-Events
 *
 * Scrollen im Menue bleibt moeglich: bewegt sich der Finger mehr als
 * MOVE_TOLERANCE Pixel oder dauert der Kontakt laenger als PRESS_TIMEOUT,
 * wird NICHT navigiert.
 * ---------------------------------------------------------------------------
 */
(function () {
  'use strict';

  var MOVE_TOLERANCE = 12; // px - darueber gilt es als Scroll-Geste
  var PRESS_TIMEOUT = 1200; // ms - darueber gilt es als Long-Press

  var startX = 0;
  var startY = 0;
  var startTime = 0;
  var startLink = null;
  var navigating = false;
  var hasPointer = 'PointerEvent' in window;

  /**
   * Liefert den Drawer-Link zum Event-Ziel zurueck - oder null, wenn der Tap
   * nicht auf einem navigierbaren Link im Header-Drawer gelandet ist.
   */
  function drawerLink(target) {
    if (!target || target.nodeType !== 1) {
      target = target && target.parentElement;
    }
    if (!target || typeof target.closest !== 'function') return null;

    var link = target.closest('a[href]');
    if (!link) return null;

    // Nur innerhalb des Header-Drawers (mobiles Burger-Menue).
    if (!link.closest('header-drawer')) return null;

    var href = link.getAttribute('href');
    if (!href) return null;

    // Anker, Mail-, Tel- und JS-Links dem Browser ueberlassen.
    if (href.charAt(0) === '#') return null;
    if (/^(mailto:|tel:|sms:|javascript:)/i.test(href)) return null;

    // Links, die bewusst in einem neuen Tab oeffnen, nicht anfassen.
    var target_ = link.getAttribute('target');
    if (target_ && target_ !== '' && target_ !== '_self') return null;
    if (link.hasAttribute('download')) return null;

    return link;
  }

  /** Erzwingt die Weiterleitung und stoppt alles Weitere. */
  function forceNavigate(link, event) {
    if (navigating) return;
    navigating = true;

    if (event) {
      event.preventDefault();
      event.stopPropagation();
      if (typeof event.stopImmediatePropagation === 'function') {
        event.stopImmediatePropagation();
      }
    }

    var url = link.href;

    // Scroll-Lock loesen, falls der Browser die Seite aus dem bfcache
    // zurueckholt (Zurueck-Button) - sonst bleibt der Body gesperrt.
    try {
      document.documentElement.removeAttribute('scroll-lock');
    } catch (e) {
      /* egal */
    }

    // Synchron navigieren. Kein rAF, kein setTimeout - jede Verzoegerung
    // gibt anderen Handlern die Chance, wieder dazwischenzugehen.
    window.location.assign(url);
  }

  var startSummary = null;

  function onDown(event) {
    // Nur Haupttaste / normaler Touch.
    if (typeof event.button === 'number' && event.button !== 0) return;

    startSummary = drawerSummary(event.target);
    startLink = drawerLink(event.target);
    if (!startLink && !startSummary) return;
    if (!startLink) {
      startX = event.clientX || 0;
      startY = event.clientY || 0;
      startTime = Date.now();
      return;
    }

    startX = event.clientX || 0;
    startY = event.clientY || 0;
    startTime = Date.now();
  }

  function onUp(event) {
    var link = startLink;
    var summaryDetails = startSummary;
    startLink = null;
    startSummary = null;

    if (!link && summaryDetails) {
      if (drawerSummary(event.target) !== summaryDetails) return;
      if (Math.abs((event.clientX || 0) - startX) > MOVE_TOLERANCE) return;
      if (Math.abs((event.clientY || 0) - startY) > MOVE_TOLERANCE) return;
      if (Date.now() - startTime > PRESS_TIMEOUT) return;
      toggleSummary(summaryDetails, event);
      return;
    }

    if (!link) return;

    // Modifier-Klicks (neuer Tab, Download, ...) unangetastet lassen.
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    if (typeof event.button === 'number' && event.button !== 0) return;

    // Das Ziel muss immer noch derselbe Link sein.
    if (drawerLink(event.target) !== link) return;

    var dx = Math.abs((event.clientX || 0) - startX);
    var dy = Math.abs((event.clientY || 0) - startY);
    if (dx > MOVE_TOLERANCE || dy > MOVE_TOLERANCE) return;
    if (Date.now() - startTime > PRESS_TIMEOUT) return;

    forceNavigate(link, event);
  }

  /**
   * Zweiter Teil des Fixes: die aufklappbaren Menue-Eintraege (Kategorien mit
   * Unterpunkten) im Drawer. Auch hier schluckte der erste Tap teilweise das
   * Aufklappen. Wir uebernehmen das Umschalten selbst und schalten die
   * native <details>-Mechanik fuer diesen Tap ab, damit nicht beides
   * gleichzeitig laeuft und sich gegenseitig aufhebt.
   *
   * Ausgenommen sind Summaries, die das Theme ueber ein on:click-Attribut
   * selbst steuert (Haupt-Burger und die Submenu-Panels bei dreistufigen
   * Menues) - die haben ihre eigene Logik in header-drawer.js.
   */
  function drawerSummary(target) {
    if (!target || target.nodeType !== 1) {
      target = target && target.parentElement;
    }
    if (!target || typeof target.closest !== 'function') return null;

    var summary = target.closest('summary');
    if (!summary) return null;
    if (!summary.closest('header-drawer')) return null;
    if (summary.hasAttribute('on:click')) return null;

    var details = summary.parentElement;
    if (!details || details.tagName !== 'DETAILS') return null;

    return details;
  }

  function toggleSummary(details, event) {
    event.preventDefault();
    event.stopPropagation();
    if (typeof event.stopImmediatePropagation === 'function') {
      event.stopImmediatePropagation();
    }

    var willOpen = !details.open;
    details.open = willOpen;

    var summary = details.querySelector('summary');
    if (summary) summary.setAttribute('aria-expanded', willOpen ? 'true' : 'false');
  }

  /** Backstop: falls pointerup nicht gefeuert hat, greift der click. */
  function onClick(event) {
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    if (typeof event.button === 'number' && event.button !== 0) return;

    var link = drawerLink(event.target);
    if (link) {
      forceNavigate(link, event);
      return;
    }

    // Nur wenn kein Pointer-Event vorausging (z.B. Tastatur-Enter), sonst
    // hat onUp bereits umgeschaltet.
    if (!hasPointer) {
      var details = drawerSummary(event.target);
      if (details) toggleSummary(details, event);
    }
  }

  /** Fallback fuer Browser ohne Pointer-Events. */
  function onTouchStart(event) {
    var touch = event.changedTouches && event.changedTouches[0];
    if (!touch) return;

    startLink = drawerLink(event.target);
    if (!startLink) return;

    startX = touch.clientX;
    startY = touch.clientY;
    startTime = Date.now();
  }

  function onTouchEnd(event) {
    var link = startLink;
    startLink = null;
    if (!link) return;

    var touch = event.changedTouches && event.changedTouches[0];
    if (!touch) return;

    if (Math.abs(touch.clientX - startX) > MOVE_TOLERANCE) return;
    if (Math.abs(touch.clientY - startY) > MOVE_TOLERANCE) return;
    if (Date.now() - startTime > PRESS_TIMEOUT) return;

    forceNavigate(link, event);
  }

  if (hasPointer) {
    document.addEventListener('pointerdown', onDown, true);
    document.addEventListener('pointerup', onUp, true);
  } else {
    document.addEventListener('touchstart', onTouchStart, true);
    document.addEventListener('touchend', onTouchEnd, true);
  }

  document.addEventListener('click', onClick, true);

  // Kommt der Nutzer per Zurueck-Button aus dem bfcache zurueck, muss die
  // Sperre wieder aufgehoben werden, sonst haengt das Menue im Navigations-
  // Zustand fest.
  window.addEventListener('pageshow', function () {
    navigating = false;
    startLink = null;
    startSummary = null;
  });
})();
